import React from 'react'
import { YStack, styled } from 'tamagui'

import { Metadata } from '@newComponents/Metadata'
import { H1, H2, H3, Paragraph, Strong } from '@newComponents/Text'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Card } from '@newComponents/Card'
import { Link, ExternalLink } from '@newComponents/Link'

export const PrivacyPolicyScreen = () => {
  return (
    <>
      <Metadata title='Privacy Policy' />

      <GradientBackgroundScreen alignItems='center'>
        <YStack gap='$8'>
          <Section>
            <H1>Privacy Policy</H1>

            <Paragraph size='$4' color='$grey7'>
              Last updated: February 24, 2026
            </Paragraph>

            <Paragraph>
              Guild Community Host Inc. ("Guild", "we", "us", or "our") is committed to
              protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your personal information
              when you use our platform at guild.host, including our website,
              mobile applications, and related services (collectively, the
              "Service").
            </Paragraph>
          </Section>

          <Section>
            <H2>1. Information We Collect</H2>

            <H3>Information You Provide</H3>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                <Strong>Account information:</Strong> When you create an account,
                we collect your email address, first name, last name, and a
                username. You may add additional verified email addresses to your
                account.
              </Paragraph>
              <Paragraph>
                <Strong>Profile information:</Strong> You may optionally provide
                a biography, location, profile photo, and background photo.
              </Paragraph>
              <Paragraph>
                <Strong>Content:</Strong> Posts, comments, Presentations, and
                images you upload or create through the Service.
              </Paragraph>
              <Paragraph>
                <Strong>Event and Guild data:</Strong> Information about Events
                you organize or attend, Guilds you create or join, and your
                participation preferences (such as in-person or virtual
                attendance).
              </Paragraph>
              <Paragraph>
                <Strong>Payment information:</Strong> When you purchase tickets
                or make donations, payment details are collected directly by
                Stripe, our payment processor. We receive transaction metadata
                (amounts, fees, payment status) but{' '}
                <Strong>
                  we do not store your credit card or payment card details
                </Strong>
                .
              </Paragraph>
              <Paragraph>
                <Strong>Communication preferences:</Strong> Your notification
                settings, email sharing preferences, and unsubscribe choices.
              </Paragraph>
            </YStack>

            <H3>Information Collected Automatically</H3>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                <Strong>Usage data:</Strong> We collect information about how you
                interact with the Service, including pages viewed, features used,
                and actions taken.
              </Paragraph>
              <Paragraph>
                <Strong>Device and browser information:</Strong> We may collect
                your browser type, operating system, and device information to
                optimize the Service.
              </Paragraph>
              <Paragraph>
                <Strong>Location data:</Strong> We may infer your approximate
                location (country) from your IP address for analytics purposes.
                We do not track your precise GPS location unless you explicitly
                provide it in your profile.
              </Paragraph>
            </YStack>
          </Section>

          <Section>
            <H2>2. How We Use Your Information</H2>

            <Paragraph>
              We use the information we collect for the following purposes:
            </Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                {'\u2022'} <Strong>Provide the Service:</Strong> Operate and
                maintain the platform, process transactions, and deliver
                features you request.
              </Paragraph>
              <Paragraph>
                {'\u2022'} <Strong>Authentication:</Strong> Verify your identity
                via email magic links or passwords and manage your sessions.
              </Paragraph>
              <Paragraph>
                {'\u2022'} <Strong>Communications:</Strong> Send you Event
                reminders, attendance confirmations, Guild updates, daily or
                weekly activity digests, and other notifications you have opted
                into.
              </Paragraph>
              <Paragraph>
                {'\u2022'} <Strong>Social features:</Strong> Display your
                profile information to other users, show your attendance at
                Events, and enable following relationships.
              </Paragraph>
              <Paragraph>
                {'\u2022'} <Strong>Content generation:</Strong> We may use AI
                services to generate social media post summaries when Guild
                organizers opt in to the Bluesky integration. This uses Event
                descriptions (not personal data) to create brief announcements.
              </Paragraph>
              <Paragraph>
                {'\u2022'} <Strong>Improvement:</Strong> Analyze usage patterns
                to improve and develop the Service.
              </Paragraph>
              <Paragraph>
                {'\u2022'} <Strong>Safety:</Strong> Detect and prevent fraud,
                abuse, and security incidents.
              </Paragraph>
            </YStack>
          </Section>

          <Section>
            <H2>3. How We Share Your Information</H2>

            <Paragraph>
              We do not sell your personal information. We share your information
              only in the following circumstances:
            </Paragraph>

            <H3>With Other Users</H3>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                {'\u2022'} Your profile name, username, bio, and photos are
                visible to other users of the Service.
              </Paragraph>
              <Paragraph>
                {'\u2022'} When you join a Guild or attend an Event, your
                membership or attendance is visible to other members.
              </Paragraph>
              <Paragraph>
                {'\u2022'} If you consent to share your email with an Event
                organizer or Guild administrator, they may access your email
                address. This sharing is optional and controlled by you at the
                time of registration.
              </Paragraph>
              <Paragraph>
                {'\u2022'} Event organizers may export attendee information, and
                Guild organizers may export member information, for users who
                have consented to share their data.
              </Paragraph>
            </YStack>

            <H3>With Third-Party Service Providers</H3>

            <Paragraph>
              We work with trusted third parties who process data on our behalf:
            </Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                <Strong>Stripe:</Strong> Processes payments for Event tickets and
                Guild donations. Stripe receives your payment details directly.
                See{' '}
                <ExternalLink href='https://stripe.com/privacy'>
                  Stripe's Privacy Policy
                </ExternalLink>
                .
              </Paragraph>
              <Paragraph>
                <Strong>Amazon Web Services (AWS):</Strong> Hosts our database
                and sends email notifications via AWS SES on our behalf.
              </Paragraph>
              <Paragraph>
                <Strong>Cloudflare:</Strong> Provides infrastructure, content
                delivery, and image storage (R2) for the Service.
              </Paragraph>
              <Paragraph>
                <Strong>Google Maps:</Strong> Displays venue locations. Your
                interaction with embedded maps is subject to{' '}
                <ExternalLink href='https://policies.google.com/privacy'>
                  Google's Privacy Policy
                </ExternalLink>
                .
              </Paragraph>
              <Paragraph>
                <Strong>Sentry:</Strong> Monitors application errors to help us
                maintain the reliability of the Service.
              </Paragraph>
              <Paragraph>
                <Strong>Google AI:</Strong> Used to generate brief social media
                summaries of Event descriptions for the optional Bluesky
                integration. No personal data is sent to AI services.
              </Paragraph>
            </YStack>

            <H3>With Social Platforms</H3>

            <Paragraph>
              If a Guild organizer enables the Bluesky integration, Event
              information (title, description, date, and link) may be
              automatically posted to the Guild's Bluesky account. Personal
              attendee data is not shared with Bluesky.
            </Paragraph>

            <H3>As Required by Law</H3>

            <Paragraph>
              We may disclose your information if required to do so by law, or if
              we believe in good faith that such disclosure is necessary to comply
              with legal obligations, protect our rights, or ensure the safety of
              our users.
            </Paragraph>
          </Section>

          <Section>
            <H2>4. Data Storage and Security</H2>

            <Paragraph>
              Your data is stored on secure servers provided by Amazon Web
              Services (database) and Cloudflare (images and assets). We employ
              industry-standard security measures, including:
            </Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                {'\u2022'} Encryption of data in transit (HTTPS/TLS)
              </Paragraph>
              <Paragraph>
                {'\u2022'} Database-level access controls and row-level security
                policies
              </Paragraph>
              <Paragraph>
                {'\u2022'} Signed URLs for image access to prevent unauthorized
                use
              </Paragraph>
              <Paragraph>
                {'\u2022'} Secure authentication with one-time email codes and
                hashed passwords
              </Paragraph>
            </YStack>

            <Paragraph>
              While we strive to protect your personal information, no method of
              transmission or storage is 100% secure. We cannot guarantee
              absolute security.
            </Paragraph>
          </Section>

          <Section>
            <H2>5. Your Rights and Choices</H2>

            <H3>Access and Update</H3>

            <Paragraph>
              You can view and update your personal information at any time
              through your account settings, including your name, bio, location,
              photos, and email addresses.
            </Paragraph>

            <H3>Delete Your Account</H3>

            <Paragraph>
              You may request deletion of your account at any time by
              contacting us at{' '}
              <ExternalLink href='mailto:hi@guild.host'>
                hi@guild.host
              </ExternalLink>
              . When your account is deleted, we permanently remove your
              profile, memberships, content, images, and associated data.
              Guilds where you are the sole member will also be deleted.
            </Paragraph>

            <H3>Email Communications</H3>

            <Paragraph>
              You can manage your notification preferences from your account
              settings. Each notification type (Event reminders, Guild updates,
              daily or weekly digests) can be individually enabled or disabled.
              You can also unsubscribe from notifications directly via the
              unsubscribe link in any email we send, without needing to sign in.
            </Paragraph>

            <H3>Data Sharing Consent</H3>

            <Paragraph>
              When attending Events or joining Guilds, you control whether your
              email address is shared with organizers. You can choose not to
              share this information at the point of registration.
            </Paragraph>
          </Section>

          <Section>
            <H2>6. Cookies and Tracking</H2>

            <Paragraph>
              We use essential cookies and local storage for authentication and
              session management. These are necessary for the Service to function
              and cannot be disabled.
            </Paragraph>

            <Paragraph>
              We collect limited analytics related to Guild Sponsorship
              interactions (views and clicks), including approximate country-level
              location inferred from your IP address. We do not use third-party
              advertising trackers or sell data to advertisers.
            </Paragraph>
          </Section>

          <Section>
            <H2>7. Data Retention</H2>

            <Paragraph>
              We retain your personal information for as long as your account is
              active or as needed to provide the Service. If you delete your
              account, your data is permanently removed, except where we are
              required to retain it for legal, accounting, or compliance
              purposes.
            </Paragraph>

            <Paragraph>
              Transaction records related to ticket purchases and donations may
              be retained for financial record-keeping purposes even after
              account deletion, as required by applicable law.
            </Paragraph>
          </Section>

          <Section>
            <H2>8. International Data Transfers</H2>

            <Paragraph>
              Our Service is operated from the United States. If you access the
              Service from outside the United States, your information may be
              transferred to and processed in countries that may have different
              data protection laws than your jurisdiction. By using the Service,
              you consent to the transfer of your information to the United
              States.
            </Paragraph>
          </Section>

          <Section>
            <H2>9. Children's Privacy</H2>

            <Paragraph>
              The Service is not directed to children under 16. We do not
              knowingly collect personal information from children under 16. If
              we become aware that we have collected personal information from a
              child under 16, we will take steps to delete that information
              promptly. If you believe a child under 16 has provided us with
              personal information, please contact us at{' '}
              <ExternalLink href='mailto:hi@guild.host'>
                hi@guild.host
              </ExternalLink>
              .
            </Paragraph>
          </Section>

          <Section>
            <H2>10. Changes to This Policy</H2>

            <Paragraph>
              We may update this Privacy Policy from time to time. If we make
              material changes, we will notify you by email or through the
              Service. Your continued use of the Service after any changes
              constitutes your acceptance of the updated Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <H2>11. Contact Us</H2>

            <Paragraph>
              If you have questions or concerns about this Privacy Policy or our
              data practices, please contact us at{' '}
              <ExternalLink href='mailto:hi@guild.host'>
                hi@guild.host
              </ExternalLink>
              .
            </Paragraph>

            <Paragraph>
              See also our{' '}
              <Link href='/legal/terms'>Terms of Service</Link>.
            </Paragraph>
          </Section>
        </YStack>
      </GradientBackgroundScreen>
    </>
  )
}

export default PrivacyPolicyScreen

const Section = styled(Card, {
  tag: 'section',
  gap: '$6',
  maxWidth: 600,
  width: '100%',
})
